import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Observable } from "rxjs";
import { debounceTime, distinctUntilChanged, startWith, switchMap } from "rxjs/operators";
import { SelectOption } from "../../core/models/select-option.model";
import { AdminService } from "../../core/services/admin.service";
import { MatAutocompleteTrigger } from "@angular/material/autocomplete";

@Component({
  selector: 'app-admin-autocomplete',
  templateUrl: './admin-autocomplete.component.html',
  styleUrls: ['./admin-autocomplete.component.scss']
})
export class AdminAutoCompleteComponent implements OnInit {
  @ViewChild(MatAutocompleteTrigger) inputAutoComplete: MatAutocompleteTrigger;
  @Input('parentForm') parentForm: FormGroup = null;
  @Input('controlName') controlName: string;
  @Input('options') options: SelectOption[] = [];
  @Input('className') className = '';
  @Input('placeholder') placeholder = '';
  @Input('required') required = false;

  @Output('selectionChanged') selectedChanged = new EventEmitter<SelectOption>();

  protected filteredOptions: Observable<SelectOption[]>;

  selectedOption: SelectOption = null;
  protected input = new FormControl<string | SelectOption>('');

  constructor(private adminService: AdminService) {
  }

  ngOnInit() {
    this.filteredOptions = this.parentForm.get(this.controlName)?.valueChanges
      .pipe(
        startWith(''),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap(val => {
          if (val !== null && val.length >= 3)
          {
            return this._filter(val || '')
          }
          else
          {
            return [];
          }
        })
      );

    // Raise event when the value of the input is cleared
    this.input.valueChanges.subscribe(value => {
      if (value === null)
      {
        value = '';
      }

      const control = this.parentForm.get(this.controlName);
      if (control)
      {
        control.patchValue((value as SelectOption).value ?? value);

        if (value === null || value === '')
        {
          this.selectedOption = null;
          control.patchValue('');
        }
      }
    });

    if (this.required)
    {
      this.input.setValidators([Validators.required, this.adminNameValidator.bind(this)]);
    }
  }

  getSearchTerm(): string {
    return this.input.value as string;
  }

  forceSearch(term: string) {
    this.input.patchValue(term);
  }

  adminNameValidator(): { [key: string]: any } | null {
    if (this.selectedOption)
    {
      return null;
    }

    return { 'adminName': true };
  }

  /**
   * Called when an admin is selected from the dropdown
   * @param option
   */
  selectOption(option: SelectOption) {
    this.selectedOption = option;
    this.parentForm.get(this.controlName)?.patchValue(option.value);
    this.selectedChanged.emit(option);
    this.input.updateValueAndValidity();
  }

  /**
   * Used to determine what value should be rendered in the field when a selection has been made
   * @param option
   */
  displayFn(option: SelectOption): string {
    return option && option.text ? option.text : '';
  }

  setOptions(options: SelectOption[]) {
    this.options = options;
  }

  filterOptions(term: string): void {
    this.filteredOptions = this._filter(term || '');
  }

  setSelectedOption(value: string) {
    const option = this.findOptionByValue(value);
    if (option !== null)
    {
      this.selectedOption = option;
      this.input.patchValue(option.value);
    }
  }

  /**
   * Presets a selection for display (e.g. restoring a persisted filter) without triggering a search or emitting selectionChanged
   * @param id
   * @param text
   */
  setInitialValue(id: string, text: string): void {
    const option: SelectOption = { value: id, text };
    this.selectedOption = option;
    this.input.patchValue(option, { emitEvent: false });
  }

  /**
   * Clears the displayed value and selection (e.g. as part of a "Clear All" action) without emitting selectionChanged
   */
  reset(): void {
    this.selectedOption = null;
    this.input.patchValue('', { emitEvent: false });
  }

  onClear() {
    this.input.patchValue('');
    const control = this.parentForm.get(this.controlName);
    if (control)
    {
      control.updateValueAndValidity();
      control.markAsUntouched();
      control.markAsPristine();
    }

    this.selectedOption = null;
    this.selectedChanged.emit(null);
  }

  findOptionByValue(value: string): SelectOption {
    let foundOption: SelectOption = null;

    this.options.forEach(option => {
      if (value.toLowerCase() === option.value.toLowerCase())
      {
        foundOption = option;
      }
    });

    return foundOption;
  }

  _filter(val: string): Observable<any[]> {
    return this.adminService.suggestAdmin(val);
  }
}
